import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import { getUser } from "../api/auth";
import toast from "react-hot-toast";
import { handleError } from "../services/error";

const TOKEN_EXPIRY_OFFSET = 1440000;

const defaultState = {
  user: null,
  setAuthUser: () => {},
  updateUser: () => {},
  logout: () => {},
  isLoggedIn: false,
  loading: true,
}

export const AuthContext = createContext(defaultState);

export const AuthContextProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    setUser(null);
    Cookies.remove('jwtToken');
  }, []);

  const fetchUserData = useCallback(async (token) => {
    try {
      const response = await getUser({ token });
      setUser(response);
    } catch (err) {
      handleError(err);
      setUser(null);
      Cookies.remove('jwtToken');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = Cookies.get('jwtToken');

    if (token) {
      try {
        const { exp } = jwtDecode(token);

        if (Date.now() < exp * 1000 - TOKEN_EXPIRY_OFFSET) {
          fetchUserData(token);
        } else {
          setUser(null);
          Cookies.remove('jwtToken');
          setLoading(false);
        }
      } catch (err) {
        console.error(err);
        toast.error('An unknown error occurred. Please try again.');
        
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [fetchUserData]);

  const setAuthUser = useCallback((userData, token = null) => {
    setUser(userData);

    if (token) {
      Cookies.set('jwtToken', token);
    }
  }, []);

  const updateUser = useCallback(updatedUserData => {
    setUser(prevUser => ({
      ...prevUser,
      ...updatedUserData,
    }));
  }, []);

  const isLoggedIn = useMemo(() => !!user, [user]);

  const contextValue = useMemo(
    () => ({
      user,
      setAuthUser,
      logout,
      isLoggedIn,
      loading,
      updateUser,
    }),
    [user, setAuthUser, logout, isLoggedIn, loading, updateUser]
  )

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  return useContext(AuthContext);
}